const LEAD_STATUSES = new Set(["novo", "contatado", "reuniao_agendada", "concluido"]);

/* Campos do perfil, em um único lugar. Os três pontos que consultam app_users
   (login, sessão do portal e painel de contatos) importam esta constante, para que
   o conjunto não volte a divergir entre arquivos. */
export const PROFILE_FIELDS = "id,role,active,company_id";

export function isExpiredSessionError(error) {
  const status = Number(error?.status || error?.statusCode || 0);
  const code = String(error?.code || "");
  const message = String(error?.message || error || "");
  return status === 401 || status === 403 || code === "PGRST301" || /\b(?:jwt|token|auth|unauthori[sz]ed|forbidden|sess[aã]o expirada)\b/i.test(message);
}

export function adminDestination(profile) {
  if (!profile?.active) throw new Error("Usuário desativado.");
  return profile.role === "gerth_admin" ? "admin/leads.html" : "dashboard.html";
}

export function resolveAdminAccess(profile) {
  if (!profile) throw new Error("Usuário sem perfil de acesso.");
  return adminDestination(profile);
}

export function buildLeadQueryState({ status = "todos", search = "" } = {}) {
  return {
    status: LEAD_STATUSES.has(status) ? status : "todos",
    search: String(search).trim(),
  };
}

export function escapePostgrestSearch(value) {
  return String(value || "")
    .normalize("NFKC")
    .trim()
    .replace(/\\/g, "\\\\")
    .replace(/([,()%*_])/g, "\\$1")
    .replace(/"/g, "\\\"");
}

export function createLatestRequestGate() {
  let current = 0;
  return {
    begin() { current += 1; return current; },
    isCurrent(token) { return token === current; },
  };
}

export function createDebouncedAction({ action, delay = 250, setTimeoutFn = setTimeout, clearTimeoutFn = clearTimeout }) {
  let timer;
  return () => {
    if (timer) clearTimeoutFn(timer);
    timer = setTimeoutFn(() => {
      timer = undefined;
      action();
    }, delay);
  };
}

export async function requireGerthAdmin({ getUser, getProfile }) {
  const user = await getUser();
  if (!user) throw new Error("Sua sessão expirou. Entre novamente.");

  const profile = await getProfile(user.id);
  if (!profile?.active || profile.role !== "gerth_admin") {
    throw new Error("Você não tem permissão para acessar os contatos.");
  }

  return { user, profile };
}

/* company_admin gerencia a equipe da própria empresa com o mesmo poder que
   gerth_admin tem sobre qualquer empresa — mas nunca os painéis internos da
   Gerth (leads, cadastro de empresas), que continuam exclusivos de
   requireGerthAdmin acima. */
export function canManageCompanyData(profile) {
  return profile?.role === "gerth_admin" || profile?.role === "company_admin";
}

export async function requireGerthOrCompanyAdmin({ getUser, getProfile }) {
  const user = await getUser();
  if (!user) throw new Error("Sua sessão expirou. Entre novamente.");

  const profile = await getProfile(user.id);
  if (!profile?.active || !canManageCompanyData(profile)) {
    throw new Error("Você não tem permissão para gerenciar a equipe.");
  }

  return { user, profile };
}

const PORTAL_ROLES = new Set(["gerth_admin", "client_manager", "seller", "company_admin"]);

export async function requirePortalUser({ getUser, getProfile }) {
  const user = await getUser();
  if (!user) throw new Error("Sua sessão expirou. Entre novamente.");

  const profile = await getProfile(user.id);
  if (!profile?.active || !PORTAL_ROLES.has(profile.role)) {
    throw new Error("Você não tem permissão para acessar o portal.");
  }

  /* Defesa em profundidade, espelhando app_users_company_scope_check no banco:
     gestor e vendedor sem empresa não têm escopo de dado algum e não devem entrar. */
  if (profile.role !== "gerth_admin" && !profile.company_id) {
    throw new Error("Você não tem permissão para acessar o portal.");
  }

  return { user, profile };
}

export function createLoginController({ signIn, getProfile, signOut = async () => {}, navigate }) {
  return {
    async submit({ email, password }) {
      const result = await signIn({ email: String(email).trim(), password });
      if (!result?.user?.id) throw new Error("E-mail ou senha inválidos.");

      let profile;
      try {
        profile = await getProfile(result.user.id);
      } catch {
        await signOut({ scope: "local" });
        throw new Error("Usuário sem perfil de acesso.");
      }
      let destination;
      try {
        destination = resolveAdminAccess(profile);
      } catch (error) {
        await signOut({ scope: "local" });
        throw error;
      }
      navigate(destination);
      return profile;
    },
  };
}

export function createLeadStore({ list, update, insertEvent, now = () => new Date().toISOString() }) {
  return {
    list(filters, page = 1, pageSize = 20) {
      return list({ ...buildLeadQueryState(filters), page, pageSize });
    },
    async updateStatus(leadId, status) {
      if (!LEAD_STATUSES.has(status)) throw new Error("Status de contato inválido.");
      if (await update(leadId, { status, updated_at: now() }) !== true) {
        throw new Error("Não foi possível confirmar a alteração. Sua sessão pode ter expirado.");
      }
    },
    async addNote(leadId, actorUserId, note) {
      const normalizedNote = String(note || "").trim();
      if (!normalizedNote) return false;
      if (await insertEvent({
        lead_id: leadId,
        actor_user_id: actorUserId,
        event_type: "observacao",
        note: normalizedNote,
      }) !== true) {
        throw new Error("Não foi possível confirmar a alteração. Sua sessão pode ter expirado.");
      }
      return true;
    },
  };
}

export function escapeHtml(value) {
  return String(value ?? "").replace(/[&<>'"]/g, (character) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    "'": "&#39;",
    '"': "&quot;",
  })[character]);
}

export function formatLeadDate(value) {
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? "—"
    : new Intl.DateTimeFormat("pt-BR", { dateStyle: "short", timeStyle: "short" }).format(date);
}

/* Formata uma data pura ("AAAA-MM-DD", sem hora) como DD/MM/AAAA, sem nunca
   passar por `new Date()`. Bug real: new Date("2026-09-30") é meia-noite UTC,
   e formatar isso com fuso de Brasília (UTC-3) devolve 29/09 — um dia antes.
   Datas puras do banco (negotiated_on, period_start/end, last_sale_date) não
   têm hora nem fuso; ler a string direto evita essa conversão por completo. */
export function formatIsoDate(value) {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value || ""));
  return match ? `${match[3]}/${match[2]}/${match[1]}` : "—";
}

export function statusLabel(status) {
  return ({
    novo: "Novo",
    contatado: "Contatado",
    reuniao_agendada: "Reunião agendada",
    concluido: "Concluído",
  })[status] || "Não informado";
}

export function renderLeadRows(leads) {
  return leads.map((lead) => `
    <tr>
      <td>${escapeHtml(formatLeadDate(lead.created_at))}</td>
      <td><span class="t-name" title="${escapeHtml(lead.company_name)}">${escapeHtml(lead.company_name)}</span><span class="t-sub">${escapeHtml(lead.sector)}</span></td>
      <td><span class="t-name">${escapeHtml(lead.contact_name)}</span><span class="t-sub">${escapeHtml(lead.email)}</span></td>
      <td>${escapeHtml(lead.phone)}</td>
      <td><select class="lead-status f-select" id="status-${escapeHtml(lead.id)}" aria-label="Status" data-status-id="${escapeHtml(lead.id)}">${["novo", "contatado", "reuniao_agendada", "concluido"].map((status) => `<option value="${status}"${lead.status === status ? " selected" : ""}>${statusLabel(status)}</option>`).join("")}</select></td>
      <td><span class="badge ${lead.notification_status === "enviada" ? "b-ok" : lead.notification_status === "falhou" ? "b-neg" : "b-warn"}">${escapeHtml(lead.notification_status || "pendente")}</span></td>
      <td><button class="btn btn-ghost btn-sm lead-detail" type="button" data-lead-id="${escapeHtml(lead.id)}">Detalhes</button></td>
    </tr>`).join("");
}
